import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import styled from "styled-components";
import { get, set, debounce } from "lodash";
import { ChromePicker } from "react-color";
import { Collapse } from "antd";
import { YAxisOptions } from "highcharts";
import { EE, ChartEditorEvent } from "@utils/helpers/events";
import { ChartSettings, RoseChart, RoseTimeseriesGroup } from "@types";
import { ThemeVarNames } from "@theme";
import {
  Button,
  Checkbox,
  Input,
  Drawer,
  Form,
  Select,
  SelectOption,
  Upload,
} from "@components/common";
import UserContainer from "@utils/state/userContainer";
import { useUserSettings } from "@service/useUserSettings";
import ot from "opentype.js";

// Debounce delay for input changes (ms)
const INPUT_DEBOUNCE_DELAY = 150;

const { Panel } = Collapse;

export type ChartEditorProps = {
  onChange: (value: ChartSettings) => void;
  settings: ChartSettings;
  roseObject: RoseTimeseriesGroup | RoseChart;
};

export function ChartEditor({
  onChange,
  settings,
  roseObject,
}: ChartEditorProps) {
  const inputRef = useRef(null);
  const timeoutRef = useRef<number>();
  const [visible, setVisible] = useState(false);
  const [chartSettings, setChartSettings] = useState<ChartSettings>(settings);
  const [draftTitle, setDraftTitle] = useState(settings?.title?.text ?? "");
  const [draftSource, setDraftSource] = useState(settings?.source ?? "");
  const [draftDatasetNames, setDraftDatasetNames] = useState<
    Record<string, string>
  >({});
  const [draftYAxisValues, setDraftYAxisValues] = useState<
    Record<number, { title?: string; min?: string; max?: string }>
  >({});
  const [showCustomFontForm, setShowCustomFontForm] = useState(false);
  const [customFontName, setCustomFontName] = useState("");
  const [customFontFile, setCustomFontFile] = useState<File | null>(null);
  const [isCreatingCustomFont, setIsCreatingCustomFont] = useState(false);
  const { user } = UserContainer.useContainer();
  const { changeSettings } = useUserSettings();

  useEffect(() => {
    EE.on(ChartEditorEvent + roseObject.key, (value: any) => {
      if (value) {
        setVisible(true);
      }
    });

    () => {
      EE.removeListener(
        ChartEditorEvent + roseObject.key,
        (chartOptions: any) => {
          if (chartOptions.isOpen) {
            setVisible(true);
          }
        },
      );
    };
  }, []);

  useEffect(() => {
    if (settings) {
      setChartSettings(settings);
      setDraftTitle(settings?.title?.text ?? "");
      setDraftSource(settings?.source ?? "");
      setDraftDatasetNames(
        Object.fromEntries(
          Object.entries(settings?.datasets ?? {}).map(([key, values]: any) => [
            key,
            values?.name ?? "",
          ]),
        ),
      );
      setDraftYAxisValues(
        Array.isArray(settings?.yAxis)
          ? settings.yAxis.reduce(
              (acc: any, axis: any) => ({
                ...acc,
                [axis.index]: {
                  title: axis?.title?.text ?? "",
                  min: axis?.min ?? "",
                  max: axis?.max ?? "",
                },
              }),
              {},
            )
          : {},
      );
    }
  }, [settings]);

  useEffect(() => {
    if (visible && inputRef.current) {
      timeoutRef.current = window.setTimeout(() => {
        inputRef.current.focus();
      }, 0);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [visible]);

  // Debounced state update for title changes - reduces re-renders during typing
  const debouncedTitleUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setChartSettings((prev) => ({
          ...prev,
          title: {
            ...prev.title,
            text: value,
          },
        }));
      }, INPUT_DEBOUNCE_DELAY),
    [],
  );

  // Debounced state update for source changes
  const debouncedSourceUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setChartSettings((prev) => ({
          ...prev,
          source: value,
        }));
      }, INPUT_DEBOUNCE_DELAY),
    [],
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedTitleUpdate.cancel();
      debouncedSourceUpdate.cancel();
    };
  }, [debouncedTitleUpdate, debouncedSourceUpdate]);

  const handleOnTitleChange = useCallback(
    (value: string) => {
      setDraftTitle(value);
      debouncedTitleUpdate(value);
    },
    [debouncedTitleUpdate],
  );

  const handleOnSourceChange = useCallback(
    (value: string) => {
      setDraftSource(value);
      debouncedSourceUpdate(value);
    },
    [debouncedSourceUpdate],
  );

  // Debounced Y-axis change handler
  const debouncedYAxisUpdate = useMemo(
    () =>
      debounce((newYAxis: Partial<YAxisOptions>, idx: number) => {
        setChartSettings((prev) => {
          const newYAxisOptions = Array.isArray(prev.yAxis)
            ? [...prev.yAxis]
            : [prev.yAxis];
          newYAxisOptions[idx as number] = {
            ...newYAxisOptions[idx as number],
            ...newYAxis,
          };
          return { ...prev, yAxis: newYAxisOptions };
        });
      }, INPUT_DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedYAxisUpdate.cancel();
    };
  }, [debouncedYAxisUpdate]);

  const handleOnYAxisChange = useCallback(
    (newYAxis: Partial<YAxisOptions>, idx: number) => {
      debouncedYAxisUpdate(newYAxis, idx);
    },
    [debouncedYAxisUpdate],
  );

  // Debounced label change handler
  const debouncedLabelUpdate = useMemo(
    () =>
      debounce((key: string, value: any) => {
        setChartSettings((prev) => {
          const newObj: any = { ...prev };
          const currDatasets: any = get(newObj, "datasets");
          currDatasets[key] = { ...currDatasets[key], name: value };
          newObj.datasets = { ...currDatasets };
          return newObj;
        });
      }, INPUT_DEBOUNCE_DELAY),
    [],
  );

  // Debounced color change handler - prevents rapid updates while dragging color picker
  const debouncedColorUpdate = useMemo(
    () =>
      debounce((color: string, key: string) => {
        setChartSettings((prev) => {
          const newObj: any = { ...prev };
          const currDatasets: any = get(newObj, "datasets");
          currDatasets[key] = { ...currDatasets[key], color };
          newObj.datasets = { ...currDatasets };
          return newObj;
        });
      }, 50), // Shorter delay for color picker for smoother feedback
    [],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedLabelUpdate.cancel();
      debouncedColorUpdate.cancel();
    };
  }, [debouncedLabelUpdate, debouncedColorUpdate]);

  const handleOnLabelChange = useCallback(
    (key: string, value: any) => {
      setDraftDatasetNames((prev) => ({ ...prev, [key]: value }));
      debouncedLabelUpdate(key, value);
    },
    [debouncedLabelUpdate],
  );

  const handleColorChange = useCallback(
    (color: any, key: any) => {
      debouncedColorUpdate(color.hex, key);
    },
    [debouncedColorUpdate],
  );

  const watermarkCheckboxChange = useCallback((e: any) => {
    setChartSettings((prev) => ({
      ...prev,
      watermark: e.target.checked,
    }));
  }, []);

  const fontChange = async (font: string) => {
    // check if the font is already in the user settings fonts array
    const settings = chartSettings;
    const existingFont = user.settings?.fonts?.find(
      (f: any) => f.name === font,
    );
    if (existingFont) {
      const font_face = new FontFace(
        existingFont.name,
        `url(${existingFont.url})`,
        {},
      );
      document.fonts.add(font_face);
      // update user settings
      font_face.load();
      settings.font_family = existingFont.name;
    } else {
      settings.font_family = font;
    }
    setChartSettings({ ...settings });
  };

  const poweredByCheckboxChange = useCallback((e: any) => {
    setChartSettings((prev) => ({
      ...prev,
      poweredBy: e.target.checked,
    }));
  }, []);

  const handleCustomFontFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (
      file &&
      (file.type === "font/ttf" ||
        file.type === "font/otf" ||
        file.name.endsWith(".ttf") ||
        file.name.endsWith(".otf") ||
        file.name.endsWith(".woff") ||
        file.name.endsWith(".woff2"))
    ) {
      const arrayBuffer = await file.arrayBuffer();
      const data = ot.parse(arrayBuffer);
      setCustomFontName(data.names.fullName.en);
      setCustomFontFile(file);
    }
  };

  const handleAddCustomFont = async () => {
    if (!customFontName.trim() || !customFontFile) {
      alert("Please provide both font name and font file");
      return;
    }

    try {
      setIsCreatingCustomFont(true);
      // Create a URL for the font file
      const fontUrl = URL.createObjectURL(customFontFile);

      const font_name = customFontName.trim();

      // Create and load the font face
      const fontFace = new FontFace(font_name, `url(${fontUrl})`);
      await fontFace.load();
      document.fonts.add(fontFace);

      // Create FormData to save the font to user settings
      const formData = new FormData();
      formData.append("font", font_name);
      formData.append("font_file", customFontFile);

      changeSettings(formData, () => {
        setCustomFontName("");
        setCustomFontFile(null);
        setShowCustomFontForm(false);
        fontChange(customFontName);
        setIsCreatingCustomFont(false);
      });
      await fontChange(customFontName);
    } catch (error) {
      console.error("Error loading custom font:", error);
      alert("Error loading font. Please try again with a different font file.");
      setIsCreatingCustomFont(false);
    }
  };

  const handleCancelCustomFont = () => {
    setCustomFontName("");
    setCustomFontFile(null);
    setShowCustomFontForm(false);
  };

  const handleOnSave = () => {
    debouncedTitleUpdate.flush();
    debouncedSourceUpdate.flush();
    debouncedLabelUpdate.flush();
    debouncedYAxisUpdate.flush();
    debouncedColorUpdate.flush();
    const nextSettings: ChartSettings = {
      ...chartSettings,
      title: {
        ...chartSettings.title,
        text: draftTitle,
      },
      source: draftSource,
    };
    if (nextSettings.datasets) {
      const datasets = { ...nextSettings.datasets };
      Object.entries(draftDatasetNames).forEach(([key, name]) => {
        datasets[key] = { ...datasets[key], name };
      });
      nextSettings.datasets = datasets;
    }
    if (Array.isArray(nextSettings.yAxis)) {
      nextSettings.yAxis = nextSettings.yAxis.map((axis: any) => {
        const draftAxis = draftYAxisValues[axis.index];
        if (!draftAxis) {
          return axis;
        }
        const parsedMin =
          draftAxis.min === undefined || draftAxis.min === ""
            ? (axis.min ?? null)
            : Number(draftAxis.min);
        const parsedMax =
          draftAxis.max === undefined || draftAxis.max === ""
            ? (axis.max ?? null)
            : Number(draftAxis.max);
        return {
          ...axis,
          title: {
            ...axis.title,
            text:
              draftAxis.title === undefined
                ? axis.title?.text
                : draftAxis.title,
          },
          min: Number.isNaN(parsedMin) ? (axis.min ?? null) : parsedMin,
          max: Number.isNaN(parsedMax) ? (axis.max ?? null) : parsedMax,
        };
      });
    }
    const formData = new FormData();
    formData.append("chart_font", nextSettings.font_family);
    changeSettings(formData);
    setChartSettings(nextSettings);
    onChange(nextSettings);
  };

  const isCustomFontSelected = () =>
    user?.settings?.fonts?.some((f) => f.name == chartSettings.font_family);

  const desiredLabels = ["name", "color"];
  return (
    <StyledDrawer
      title="Chart Editor"
      placement="right"
      onClose={() => setVisible(false)}
      closable
      open={visible}
      contentWrapperStyle={{
        width: "100%",
        maxWidth: "650px",
        position: "absolute",
      }}
    >
      <StyledContent>
        <Form layout="vertical" onFinish={() => handleOnSave()}>
          <Form.Item label="Chart Title">
            <Input
              type="text"
              size="middle"
              value={draftTitle}
              onChange={(e: any) => handleOnTitleChange(e.target.value)}
              ref={inputRef}
              autoFocus
            />
          </Form.Item>
          <Form.Item label="Source">
            <Input
              type="text"
              size="middle"
              value={draftSource}
              onChange={(e: any) => handleOnSourceChange(e.target.value)}
            />
          </Form.Item>
          <Collapse
            style={{ marginBottom: "24px" }}
            defaultActiveKey={Object.keys(chartSettings?.datasets ?? {})}
          >
            {Object.entries(chartSettings?.datasets ?? {}).map(
              ([key, values]) => (
                <Panel header={key} key={key}>
                  <Form.Item key={key}>
                    {Object.entries(values).map(([field, data]) =>
                      desiredLabels.includes(field) ? (
                        <Form.Item label={field} key={`${key}-${field}`}>
                          {field === "color" ? (
                            <div>
                              <ChromePicker
                                disableAlpha={true}
                                color={data}
                                onChange={(color: any) => {
                                  handleColorChange(color, key);
                                }}
                              />
                            </div>
                          ) : (
                            <Input
                              type="text"
                              size="middle"
                              value={draftDatasetNames[key] ?? data}
                              onChange={(e: any) =>
                                handleOnLabelChange(key, e.target.value)
                              }
                            />
                          )}
                        </Form.Item>
                      ) : null,
                    )}
                  </Form.Item>
                </Panel>
              ),
            )}
          </Collapse>
          <Collapse style={{ marginBottom: "24px" }}>
            {Array.isArray(chartSettings.yAxis) ? (
              <Panel header="Y Axes" key="yAxes">
                <Collapse>
                  {chartSettings.yAxis.map(
                    (
                      axis, // map all settings.yAxis to form items
                    ) =>
                      axis.visible ? (
                        <Panel header={axis.defaultLabel} key={axis.index}>
                          <Form.Item label="Axis Title">
                            <Input
                              type="text"
                              size="middle"
                              value={
                                draftYAxisValues[axis.index]?.title ??
                                axis.title.text ??
                                ""
                              }
                              onChange={(e: any) => {
                                const nextValue = e.target.value;
                                setDraftYAxisValues((prev) => ({
                                  ...prev,
                                  [axis.index]: {
                                    ...prev[axis.index],
                                    title: nextValue,
                                  },
                                }));
                                handleOnYAxisChange(
                                  {
                                    title: {
                                      ...axis.title,
                                      text: nextValue,
                                    },
                                  },
                                  axis.index,
                                );
                              }}
                            />
                          </Form.Item>
                          <Form.Item label="Axis Min">
                            <Input
                              type="number"
                              size="middle"
                              value={
                                draftYAxisValues[axis.index]?.min ??
                                axis.min ??
                                ""
                              }
                              onChange={(e: any) => {
                                const rawValue = e.target.value;
                                setDraftYAxisValues((prev) => ({
                                  ...prev,
                                  [axis.index]: {
                                    ...prev[axis.index],
                                    min: rawValue,
                                  },
                                }));
                                if (
                                  rawValue === "" ||
                                  !Number.isNaN(Number(rawValue))
                                ) {
                                  handleOnYAxisChange(
                                    {
                                      min:
                                        rawValue === ""
                                          ? null
                                          : Number(rawValue),
                                    },
                                    axis.index,
                                  );
                                }
                              }}
                            />
                          </Form.Item>
                          <Form.Item label="Axis Max">
                            <Input
                              type="number"
                              size="middle"
                              value={
                                draftYAxisValues[axis.index]?.max ??
                                axis.max ??
                                ""
                              }
                              onChange={(e: any) => {
                                const rawValue = e.target.value;
                                setDraftYAxisValues((prev) => ({
                                  ...prev,
                                  [axis.index]: {
                                    ...prev[axis.index],
                                    max: rawValue,
                                  },
                                }));
                                if (
                                  rawValue === "" ||
                                  !Number.isNaN(Number(rawValue))
                                ) {
                                  handleOnYAxisChange(
                                    {
                                      max:
                                        rawValue === ""
                                          ? null
                                          : Number(rawValue),
                                    },
                                    axis.index,
                                  );
                                }
                              }}
                            />
                          </Form.Item>
                        </Panel>
                      ) : null,
                  )}
                </Collapse>
              </Panel>
            ) : null}
          </Collapse>
          <div className="flex justify-between w-full mb-2">
            <FontFamilyLabel>Font Family</FontFamilyLabel>
            <Button
              type="ghost"
              size="small"
              onClick={() => setShowCustomFontForm(!showCustomFontForm)}
            >
              {showCustomFontForm ? "Cancel" : "Add Custom Font"}
            </Button>
          </div>
          <Form.Item
            className="w-full"
            wrapperCol={{ style: { width: "100%" } }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Select
                style={{ flex: 1 }}
                value={chartSettings.font_family ?? "Inter"}
                onChange={(font) => {
                  fontChange(font.toString());
                }}
              >
                <SelectOption value="Inter">Default</SelectOption>
                <SelectOption value="monospace">Monospace</SelectOption>
                {user?.settings?.fonts?.map((font) => (
                  <SelectOption value={font.name} key={font.id}>
                    {font.name}
                  </SelectOption>
                ))}
              </Select>
            </div>
            {isCustomFontSelected() && (
              <div className="text-xs px-1 pt-2 text-secondary-text flex items-center justify-between">
                <span>
                  Install this font on your system for proper rendering in
                  exported charts. <br />
                  {chartSettings.font_family &&
                    user?.settings?.fonts?.find(
                      (f) => f.name === chartSettings.font_family,
                    ) && (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const font = user.settings?.fonts?.find(
                            (f) => f.name === chartSettings.font_family,
                          );
                          if (font?.url) {
                            // Fetch the font file and create a blob for download
                            fetch(font.url)
                              .then((response) => response.blob())
                              .then((blob) => {
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = font.name + ".ttf";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              })
                              .catch((error) => {
                                console.error("Error downloading font:", error);
                              });
                          }
                        }}
                        style={{
                          fontSize: "12px",
                          textDecoration: "underline",
                          color: "inherit",
                        }}
                      >
                        Download
                      </a>
                    )}
                </span>
              </div>
            )}
          </Form.Item>
          {showCustomFontForm && (
            <div
              style={{
                marginBottom: "24px",
                padding: "16px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
            >
              <Form.Item label="Font File" style={{ marginBottom: "16px" }}>
                <Input
                  type="file"
                  accept=".ttf,.otf,.woff,.woff2"
                  onChange={handleCustomFontFileChange}
                />
              </Form.Item>
              {customFontName && (
                <Form.Item
                  label="Font Name"
                  style={{ marginBottom: "16px" }}
                  classNamew-full
                >
                  <div className="w-full px-2 py-1 bg-primary-bg rounded-sm">
                    <span className="text-primary-text">{customFontName}</span>
                  </div>
                  <div className="pt-2 text-xs text-secondary-text">
                    <span>
                      Note: You must install this font locally on your system
                      for it to render properly in exported charts.
                    </span>
                  </div>
                </Form.Item>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  type="ghost"
                  size="middle"
                  onClick={handleCancelCustomFont}
                  disabled={isCreatingCustomFont}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="middle"
                  onClick={handleAddCustomFont}
                  disabled={
                    !customFontName.trim() ||
                    !customFontFile ||
                    isCreatingCustomFont
                  }
                >
                  {isCreatingCustomFont ? "Loading..." : "Add Font"}
                </Button>
              </div>
            </div>
          )}
          <Form.Item
            label="Watermark"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "24px",
            }}
          >
            <Checkbox
              checked={chartSettings.watermark}
              onChange={(e) => watermarkCheckboxChange(e)}
              style={{
                padding: "0 0 12px",
              }}
            />
          </Form.Item>
          <Form.Item
            label="Credit"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: "24px",
            }}
          >
            <Checkbox
              checked={chartSettings.poweredBy}
              onChange={(e) => poweredByCheckboxChange(e)}
              style={{
                padding: "0 0 12px",
              }}
            />
          </Form.Item>
          <RightContainerButtons>
            <Form.Item>
              <Button
                type="ghost"
                onClick={() => {
                  setVisible(false);
                }}
              >
                Close
              </Button>
            </Form.Item>
            <Form.Item shouldUpdate>
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  setVisible(false);
                }}
              >
                Save
              </Button>
            </Form.Item>
          </RightContainerButtons>
        </Form>
      </StyledContent>
    </StyledDrawer>
  );
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    display: flex;
    flex-direction: row;
    padding-left: 0;
    padding-bottom: 0;
  }
`;

const StyledContent = styled.div`
  position: relative;
  width: 100%;
  padding: 30px 40px;
`;

const FontFamilyLabel = styled.span`
  color: var(${ThemeVarNames.PrimaryText});
`;

const RightContainerButtons = styled.div`
  padding-top: 20px;
  justify-content: flex-end;
  display: flex;
  flex-direction: "row";
  gap: 10px;
`;
